import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Heart } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import AddToCartModal from './AddToCartModal';
import { getColorHex, isLightColor } from '../utils/colorMap';
import { useWishlist } from '../context/WishlistContext';
import { isAdminMode } from '../utils/adminMode';
import { resolveImageUrl, optimizedImageUrl } from '../utils/imageUrl';
import { ViewersBadge } from './ViewersBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GarmentCard = ({ garment, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const { isInWishlist, toggleWishlist } = useWishlist();
  const admin = isAdminMode();

  const images = Array.isArray(garment.images) && garment.images.length > 0 ? garment.images : (garment.image_url ? [garment.image_url] : []);
  const primary = images[0];
  const secondary = images[1] || images[0];

  // Precarga el vídeo del producto cuando la tarjeta aparece en pantalla
  // → cuando la clienta hace clic, el vídeo ya está cacheado y arranca al instante
  const cardRef = useRef(null);
  useEffect(() => {
    if (!garment.video_url || !cardRef.current || typeof IntersectionObserver === 'undefined') return;
    const url = resolveImageUrl(garment.video_url);
    let started = false;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !started) {
          started = true;
          // Descarga solo los primeros 3 MB → suficiente para arrancar reproducción al hacer clic
          fetch(url, { headers: { Range: 'bytes=0-3000000' }, cache: 'force-cache' }).catch(() => {});
          io.disconnect();
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.1 });
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, [garment.video_url]);

  const handleDelete = () => {
    onDelete(garment.id);
    setShowDeleteDialog(false);
  };

  const isSoldOut = garment.total_stock === 0 || garment.stock === 0;

  return (
    <>
      <div
        ref={cardRef}
        className="group relative bg-white"
        data-testid={`garment-card-${garment.id}`}
      >
        {/* Image */}
        <div
          onClick={() => navigate(`/product/${garment.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/product/${garment.id}`);
            }
          }}
          role="button"
          tabIndex={0}
          className="relative block w-full aspect-[3/4] overflow-hidden bg-neutral-100 cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
          data-testid={`view-details-${garment.id}`}
        >
          {primary && (
            <img
              src={optimizedImageUrl(primary, 400)}
              srcSet={`${optimizedImageUrl(primary, 400)} 400w, ${optimizedImageUrl(primary, 800)} 800w`}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              alt={garment.name}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
              data-testid={`garment-image-${garment.id}`}
              loading="lazy"
              decoding="async"
              onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = '1'; e.currentTarget.removeAttribute('srcset'); e.currentTarget.src = resolveImageUrl(primary) || '/jrf-placeholder.jpg'; } }}
            />
          )}
          {secondary && secondary !== primary && (
            <img
              src={optimizedImageUrl(secondary, 400)}
              srcSet={`${optimizedImageUrl(secondary, 400)} 400w, ${optimizedImageUrl(secondary, 800)} 800w`}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              alt={`${garment.name} alt`}
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              loading="lazy"
              decoding="async"
              onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = '1'; e.currentTarget.removeAttribute('srcset'); e.currentTarget.src = resolveImageUrl(secondary) || '/jrf-placeholder.jpg'; } }}
            />
          )}

          <ViewersBadge garmentId={garment.id} compact />

          {/* Badges (top-left) */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 pointer-events-none">
            {garment.sale_price != null && garment.price != null && garment.sale_price < garment.price && (
              <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                -{Math.round(((garment.price - garment.sale_price) / garment.price) * 100)}%
              </span>
            )}
            {garment.featured && (
              <span className="bg-white text-black text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                New
              </span>
            )}
            {isSoldOut && (
              <span className="bg-black text-white text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1">
                Agotado
              </span>
            )}
          </div>

          {/* Quick add / Wishlist (top-right on hover) */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWishlist(garment);
            }}
            className="absolute top-3 right-3 bg-white/95 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            data-testid={`wishlist-toggle-${garment.id}`}
            aria-label="Añadir a deseos"
          >
            <Heart
              className={`w-4 h-4 transition-colors ${
                isInWishlist(garment.id) ? 'text-black fill-black' : 'text-black'
              }`}
              strokeWidth={1.5}
            />
          </button>

          {/* Quick Add bar (appears on hover) */}
          {!isSoldOut && (
            <div
              className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddToCartModal(true);
                }}
                className="w-full bg-white text-black text-xs font-bold uppercase tracking-[0.2em] py-3 hover:bg-black hover:text-white transition-colors"
                data-testid={`add-to-cart-${garment.id}`}
              >
                Añadir al carrito
              </button>
            </div>
          )}
        </div>

        {/* Content below image */}
        <div className="pt-4 pb-2">
          <div className="flex items-start justify-between gap-3">
            <h3
              className="text-sm font-semibold uppercase tracking-wide text-black leading-snug"
              data-testid={`garment-name-${garment.id}`}
            >
              {garment.name}
            </h3>
            {garment.price != null && (
              garment.sale_price != null && garment.sale_price < garment.price ? (
                <div
                  className="flex items-center gap-2 whitespace-nowrap"
                  data-testid={`garment-price-${garment.id}`}
                >
                  <span className="text-sm font-semibold text-red-600">{garment.sale_price.toFixed(2)}€</span>
                  <span className="text-xs text-neutral-400 line-through">{garment.price.toFixed(2)}€</span>
                </div>
              ) : (
                <p
                  className="text-sm font-semibold text-black whitespace-nowrap"
                  data-testid={`garment-price-${garment.id}`}
                >
                  {garment.price.toFixed(2)}€
                </p>
              )
            )}
          </div>

          {/* Category subtle */}
          <p
            className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1"
            data-testid={`garment-category-${garment.id}`}
          >
            {garment.category}
          </p>

          {/* Color swatches */}
          {garment.colors && garment.colors.length > 0 && (
            <div className="mt-3 flex items-center gap-1.5">
              {garment.colors.slice(0, 5).map((color, idx) => (
                <span
                  key={idx}
                  title={color}
                  className="w-3.5 h-3.5 rounded-full inline-block"
                  style={{
                    backgroundColor: getColorHex(color),
                    border: isLightColor(color) ? '1px solid #D1D5DB' : '1px solid transparent',
                  }}
                />
              ))}
              {garment.colors.length > 5 && (
                <span className="text-[11px] text-neutral-500 ml-1">+{garment.colors.length - 5}</span>
              )}
            </div>
          )}

          {/* Admin actions */}
          {admin && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(garment);
                }}
                className="flex-1 border border-black text-black text-[10px] font-bold uppercase tracking-[0.2em] py-2 hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1"
                data-testid={`edit-garment-${garment.id}`}
              >
                <Edit className="w-3 h-3" strokeWidth={2} /> Editar
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
                className="flex-1 border border-neutral-300 text-neutral-600 text-[10px] font-bold uppercase tracking-[0.2em] py-2 hover:border-black hover:text-black transition-colors flex items-center justify-center gap-1"
                data-testid={`delete-garment-${garment.id}`}
              >
                <Trash2 className="w-3 h-3" strokeWidth={2} /> Eliminar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={showAddToCartModal}
        onClose={() => setShowAddToCartModal(false)}
        garment={garment}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="rounded-none border border-black">
          <AlertDialogHeader>
            <div className="w-12 h-12 bg-red-50 border border-red-200 rounded-full flex items-center justify-center mb-3 mx-auto">
              <Trash2 className="w-6 h-6 text-red-600" strokeWidth={1.5} />
            </div>
            <AlertDialogTitle className="text-center text-2xl font-black uppercase tracking-tight">
              ¿Eliminar prenda?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-neutral-700 leading-relaxed pt-2">
              Vas a eliminar <strong className="text-black">&quot;{garment.name}&quot;</strong> permanentemente del catálogo.
              <br />
              <span className="text-red-600 font-medium block mt-2">⚠️ Esta acción no se puede deshacer.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel
              data-testid="cancel-delete"
              className="w-full sm:w-auto rounded-none border-neutral-300 hover:bg-neutral-100 text-xs font-bold uppercase tracking-[0.2em] py-3"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="w-full sm:flex-1 bg-red-600 hover:bg-red-700 text-white rounded-none text-xs font-bold uppercase tracking-[0.2em] py-3"
              data-testid="confirm-delete"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GarmentCard;
